import { useEffect, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { Icon } from '../components/Icon'
import { ticketStatusLabel } from '../domain/catalog'
import type { Aggregator } from '../domain/types'
import { scheduleDate, scheduleInstant, scheduleTime } from '../domain/scheduling'

interface ReportProps {
  items: Aggregator[]
  locationName: string
  filters: string[]
  page: number
  totalPages: number
  queueNames: Record<number, string>
}

const sessionLabel = (start?: string | null, end?: string | null) => {
  if (!start || !Number.isFinite(scheduleInstant(start))) {return '—'}
  const day = scheduleDate(new Date(scheduleInstant(start))).split('-').reverse().join('/')
  return `${day} · ${scheduleTime(start)}${end && Number.isFinite(scheduleInstant(end)) ? `–${scheduleTime(end)}` : ''}`
}

export function JourneyListPrintButton(props: ReportProps & { disabled?: boolean }) {
  const [report, setReport] = useState<(ReportProps & { generatedAt: string }) | null>(null)
  const [error, setError] = useState(false)
  const cleanupRef = useRef<(() => void) | null>(null)
  useEffect(() => () => {cleanupRef.current?.()}, [])

  const print = () => {
    if (props.disabled || !props.items.length) {return}
    cleanupRef.current?.()
    const cleanup = () => {
      window.removeEventListener('afterprint', cleanup)
      document.body.classList.remove('fz-list-printing')
      cleanupRef.current = null
      setReport(null)
    }
    cleanupRef.current = cleanup
    window.addEventListener('afterprint', cleanup, { once: true })
    document.body.classList.add('fz-list-printing')
    setError(false)
    // eslint-disable-next-line @eslint-react/dom-no-flush-sync -- O relatório precisa estar no DOM antes do snapshot síncrono de window.print().
    flushSync(() => setReport({ ...props, generatedAt: new Date().toLocaleString('pt-BR', { timeZone: 'America/Maceio' }) }))
    try {window.print()} catch {cleanup(); setError(true)}
  }

  return <>
    <button type="button" className="fz-btn fz-btn--ghost" disabled={props.disabled || !props.items.length} onClick={print}
      title="Imprimir a página atual com os filtros selecionados"><Icon name="print" size={16} /> Imprimir</button>
    {error && <span role="alert">Não foi possível imprimir. Tente novamente.</span>}
    {report && createPortal(<section className="fz-list-print-report" aria-label="Relatório de atendimentos">
      <header><h1>{report.locationName}</h1><h2>Atendimentos</h2>
        <p>{report.filters.join(' · ')}</p>
        <p>Página {report.page} de {report.totalPages} · {report.items.length} jornada{report.items.length === 1 ? '' : 's'} · Emitido em {report.generatedAt}</p>
      </header>
      <table><thead><tr>
        <th>Jornada</th><th>Cliente</th><th>Etapa / Serviço</th><th>Fila</th><th>Sessão</th>
        <th>Prioridade</th><th>Previsão</th><th>Situação</th><th>Atendente</th>
      </tr></thead><tbody>
        {report.items.flatMap((journey) => journey.tickets.map((ticket) => <tr key={`${journey.aggregatorId}:${ticket.ticketId}`}>
          <td>{journey.friendlyCode || ticket.codigoTicket || '—'}</td>
          <td><strong>{journey.cliente.nome}</strong><span>{journey.cliente.email}</span><span>{journey.cliente.telefone}</span></td>
          <td>Etapa {ticket.sequence + 1}<strong>{ticket.nomeServico}</strong></td>
          <td>{ticket.serviceConfigName || (ticket.serviceConfigId != null ? report.queueNames[ticket.serviceConfigId] : null) || ticket.sala || '—'}</td>
          <td>{sessionLabel(ticket.sessionStart, ticket.sessionEnd)}</td>
          <td>{journey.prioridade === 2 ? '+80' : journey.prioridade > 0 ? 'Preferencial' : 'Normal'}</td>
          <td>{ticket.prevision || '—'}</td><td>{ticketStatusLabel(ticket.status)}</td><td>{ticket.resourceNome || '—'}</td>
        </tr>))}
      </tbody></table>
    </section>, document.body)}
  </>
}
